import { UISref, useRouter } from "@uirouter/react";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { Loader } from "../../../components/loader";
import { Table } from "../../../components/table";
import { TableHeaders } from "../../../components/table/Head";
import { Widget } from "../../../components/widget";
import { Pageable } from "../../../services/pageable";
import { AppStore } from "../../../store";
import { useFetchable } from "../../../store/common/fetchable";
import { validationFields } from "../../../store/common/models/fields";
import { VaultActions } from "../../../store/vaults/actions";
import { VaultsDTO } from "../../../store/vaults/models/dto";
import { Vaults } from "../../../store/vaults/reducer";
import { colours } from "../../../styles/colours";
import { VaultsRoutes as VaultRoutes } from "./../routes";
import { NoVaults } from "./empty";

type SingleVaults = VaultsDTO.Responses.GetSingleVault.T;
type fieldNames = keyof SingleVaults;

function useHeaders(): TableHeaders<fieldNames> {
  const friendlyName = validationFields.friendlyName.useName();
  const createdDate = validationFields.createdDate.useName();
  const numberOfEntries = validationFields.numberOfEntries.useName();

  return useMemo(
    () => [
      {
        name: "friendlyName",
        title: friendlyName,
      },
      {
        name: "createdDate",
        title: createdDate,
      },
      {
        name: "numberOfEntries",
        title: numberOfEntries,
      },
      {
        name: "id",
        title: "",
        centered: true,
        hiddenMobile: true,
      },
    ],
    [friendlyName, createdDate, numberOfEntries]
  );
}

export const AllVaults = () => {
  const data = useFetchable(
    Vaults.vaults,
    useMemo<Pageable.T<keyof VaultsDTO.Responses.GetSingleVault.T>>(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
        sorting: {
          direction: "desc",
          field: "createdDate",
        },
      }),
      []
    )
  );

  return (
    <Loader data={[data]}>
      {([fulfilled]) => <PresentationalAllVaults vaultsList={fulfilled} />}
    </Loader>
  );
};

const PresentationalAllVaults: FunctionComponent<{
  vaultsList: VaultsDTO.Responses.GetAllVaults.T;
}> = ({ vaultsList }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const headers = useHeaders();

  const pageableState = useSelector<
    AppStore,
    AppStore["vaults"]["all"]["pagination"]
  >((store) => store.vaults.all.pagination);

  const onPaginationChange = (x: Pageable.T<fieldNames>) =>
    dispatch(VaultActions.setAllVaultsPagination(x));

  return (
    <Widget
      title="All Vaults"
      adornment={
        vaultsList.pageable.totalElements > 0 ? (
          <UISref to={VaultRoutes.names.newVault}>
            <a>
              <PrimaryButton>
                <Icon type="plus" />
              </PrimaryButton>
            </a>
          </UISref>
        ) : undefined
      }
    >
      {vaultsList.pageable.totalElements < 1 ? (
        <NoVaults />
      ) : (
        <Table
          data={vaultsList.content}
          head={headers}
          renderFields={{
            id: ({ id }) => (
              <UISref to={VaultRoutes.names.singleVault} params={{ id }}>
                <a>
                  <PrimaryButton variant="contained">
                    <Icon
                      type="export"
                      rotateAmount={270}
                      colour1={colours.white}
                    />
                  </PrimaryButton>
                </a>
              </UISref>
            ),
            createdDate: ({ createdDate }) => (
              <>{moment.utc(createdDate).local().format("yyyy-MM-DD HH:mm")}</>
            ),
          }}
          rowOnClick={({ id }) => {
            router.stateService.go(VaultRoutes.names.singleVault, { id });
          }}
          pagination={vaultsList.pageable}
          onPaginationChange={onPaginationChange}
          pageableState={pageableState}
        />
      )}
    </Widget>
  );
};
